<template>
  <v-btn
    :color="getThemeColor()"
    :disabled="!isActiveProp"
    :fab="type === 'icon'"
    :icon="iconTypes"
    :large="size === 'large'"
    :outlined="isOutlinedProp && type !== 'simpleIcon' && type !== 'text'"
    :rounded="checkRounded"
    :small="size === 'small'"
    :style="getButtonStyle()"
    :text="type === 'text'"
    :x-large="size === 'x-large'"
    :x-small="size === 'x-small'"
    elevation="4"
    @click="onClick"
  >
    <label v-if="preIcon && !iconTypes" :style="{ 'font-size': size === 'normal' ? 'unset' : size }">{{ label }}</label>
    <v-icon
      v-if="showIcon"
      :class="[{ 'ms-2': type === 'flatIcon' && preIcon }, { 'me-2': type === 'flatIcon' && !preIcon }]"
      :color="getIconThemeColor()"
      :disabled="!isActiveProp"
      :large="size === 'large'"
      :small="size === 'small'"
      :style="getIconStyle()"
      :x-large="size === 'x-large'"
      :x-small="size === 'x-small'"
    >
      {{ iconName }}
    </v-icon>
    <label v-if="!preIcon && !iconTypes" :style="{ 'font-size': size === 'normal' ? 'unset' : size }">{{
      label
    }}</label>
  </v-btn>
</template>

<script lang="ts">
import { computed, createComponent, PropType, Wrapper } from "vue-function-api";
export const KTZN_DEFAULT_COLOR = "#E95532";

export default createComponent({
  name: "KtznButton",
  props: [
    "label",
    "isActive",
    "isOutlined",
    "themeColor",
    "iconThemeColor",
    "iconName",
    "size",
    "rounded",
    "type",
    "preIcon",
    "iconStyle",
  ] as any as PropType<{
    type: string; // button type can be flat(default)/flatIcon/text/icon/simpleIcon
    label: string; // Button label
    isActive: boolean; // To determine if button is disabled
    isOutlined: false; // For outlined button
    themeColor: string; // Button Color
    iconThemeColor: string; // Icon Color
    size: string; // Button size xSmall/small/large/xLarge OR (default: unset)
    rounded: boolean; // Rounded button only for flat & flatIcon (default: false)
    iconStyle: string;
    iconName: {
      type: string;
      default: "";
    }; // Button iconName
    preIcon: {
      type: boolean;
      default: true;
    }; // Add icon for flatIcon type before the text or after that
  }>,
  setup(props, context) {
    // Props as computed
    const isActiveProp: Wrapper<boolean> = computed(() => props.isActive === true || props.isActive === undefined);
    const isOutlinedProp: Wrapper<boolean> = computed(() => props.isOutlined);
    const checkRounded: Wrapper<boolean> = computed(
      () => (props.type === "flat" || props.type === "flatIcon") && props.rounded
    );
    const showIcon: Wrapper<boolean> = computed(
      () => props.type === "icon" || props.type === "flatIcon" || props.type === "simpleIcon"
    );
    const iconTypes: Wrapper<boolean> = computed(() => props.type === "icon" || props.type === "simpleIcon");

    /* return default color or themeColor if already passed*/
    const getThemeColor = () => {
      if (props.themeColor) return props.themeColor;
      else return KTZN_DEFAULT_COLOR;
    };

    const getIconThemeColor = () => {
      if (props.iconThemeColor) return props.iconThemeColor;
      else return KTZN_DEFAULT_COLOR;
    };

    /* the color of icon when the outline is false */
    const getIconStyle = () => {
      if (props.type === "icon") {
        return props.iconStyle ? props.iconStyle : "font-size: 24px !important";
      }

      if (!isOutlinedProp.value && props.type !== "simpleIcon") {
        if (props.isActive) {
          return `color: white;`;
        } else if (!props.isActive) {
          return `color: #8b8b8b;`;
        }
      }
    };

    const getButtonStyle = () => {
      if (isOutlinedProp.value) {
        return "";
      }
      if (props.type !== "simpleIcon" && props.type !== "text") {
        if (isActiveProp.value) {
          /* when the outline is false get color based on theme */
          return `color: white; background-color: ${getThemeColor()};`;
        } else if (!isActiveProp.value) {
          /* for disable mode when the outline is false */
          return "color: #8b8b8b !important; background-color: #e0e0e0 !important";
        }
      }
      if (props.type === "text") {
        if (isActiveProp.value) {
          /* for text type to get color based on theme */
          return `color: ${getThemeColor()};`;
        } else if (!isActiveProp.value) {
          /* for text type disable mode */
          return "color: #8b8b8b !important;";
        }
      } else if (props.type === "simpleIcon") {
        if (isActiveProp.value) {
          /* for simpleIcon type */
          return `color: ${getThemeColor()} !important;`;
        } else if (!isActiveProp.value) {
          /* for simpleIcon type disable mode */
          return `color: #e0e0e0 !important;`;
        }
      } else {
        return "";
      }
    };

    // Actions
    const onClick = () => {
      context.emit("onClick");
    };

    return {
      isActiveProp,
      isOutlinedProp,
      checkRounded,
      showIcon,
      iconTypes,
      onClick,
      getThemeColor,
      getIconStyle,
      getButtonStyle,
      getIconThemeColor,
    };
  },
});
</script>
